import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, Typography, Avatar, Slider, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PageHeader from '../../components/PageHeader';
import SearchBar from '../../components/SearchBar';
import DataTable from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import { formatCurrency, formatPhone } from '../../utils/formatters';
import { getAstrologers } from '../../api/endpoints/astrologers';
import { API_URL } from '../../utils/url';

const AstrologerList = () => {
  const navigate = useNavigate();
  const [astrologers, setAstrologers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [kycFilter, setKycFilter] = useState('all');
  const [ratingRange, setRatingRange] = useState([0, 5]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchAstrologers = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (kycFilter !== 'all') params.kycStatus = kycFilter;
      if (ratingRange[0] > 0) params.minRating = ratingRange[0];
      if (ratingRange[1] < 5) params.maxRating = ratingRange[1];
      const res = await getAstrologers(params);
      setAstrologers(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load astrologers');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, kycFilter, ratingRange]);

  useEffect(() => {
    fetchAstrologers();
  }, [fetchAstrologers]);

  const columns = [
    { id: 'name', label: 'Astrologer', render: (row) => (
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar src={API_URL?.API_BASE_URL+ row.userId?.avatar || API_URL?.API_BASE_URL+row.avatar} sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14 }}>{(row.userId?.avatar || row.avatar) ? '' : (row.userId?.name || row.name || '').split(' ').map(n => n[0]).join('')}</Avatar>
        <Box>
          <Typography variant="body2" fontWeight={500}>{row.userId?.name || row.name}</Typography>
          <Typography variant="caption" color="text.secondary">{formatPhone(row.userId?.phone || row.phone)}</Typography>
        </Box>
      </Stack>
    )},
    { id: 'specializations', label: 'Specialization', render: (row) => Array.isArray(row.specializations) ? row.specializations.join(', ') : row.specializations || '-' },
    { id: 'rating', label: 'Rating', render: (row) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography fontWeight={600} sx={{ color: (row.rating || 0) >= 4.5 ? '#4CAF50' : (row.rating || 0) >= 4 ? '#FF9800' : '#F44336' }}>
          {(row.rating || 0).toFixed(1)}
        </Typography>
        <Typography variant="caption" color="text.secondary">/ 5</Typography>
      </Box>
    )},
    { id: 'experience', label: 'Experience', render: (row) => `${row.experience || 0} yrs` },
    { id: 'kycStatus', label: 'KYC', render: (row) => <StatusChip status={row.kycStatus} /> },
    { id: 'isLive', label: 'Live', render: (row) => <StatusChip status={row.isLive ? 'online' : 'offline'} /> },
    { id: 'actions', label: '', render: (row) => (
      <Tooltip title="View Details">
        <IconButton size="small" onClick={() => navigate(`/astrologers/${row._id}`)}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )},
  ];

  return (
    <Box>
      <PageHeader title="Astrologers" subtitle="Manage all registered astrologers" />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <SearchBar value={search} onChange={setSearch} placeholder="Search astrologers..." />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>KYC Status</InputLabel>
          <Select value={kycFilter} label="KYC Status" onChange={(e) => setKycFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ minWidth: 200, px: 1 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>Rating Range: {ratingRange[0]} - {ratingRange[1]}</Typography>
          <Slider value={ratingRange} onChange={(e, v) => setRatingRange(v)} min={0} max={5} step={0.5} size="small" />
        </Box>
      </Stack>
      <DataTable
        columns={columns}
        rows={astrologers}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        loading={loading}
        emptyMessage="No astrologers found"
      />
    </Box>
  );
};

export default AstrologerList;